/**
 * THE INBOX — A DURABLE RECORD THE OWNER CAN READ, MARK, AND FOLLOW.
 *
 * FOUR THINGS THIS SURFACE REFUSES TO DO, EACH BECAUSE THE ALTERNATIVE IS A
 * CLAIM THE BACKEND CANNOT SUPPORT:
 *
 *   1. IT DOES NOT SHOW A PAGE AS THOUGH IT WERE THE WHOLE INBOX. The route is
 *      cursored, and the two counts on this screen are computed over every row
 *      for this Owner rather than over the page. Without them, twenty-five
 *      notifications and two hundred look identical, which is the failure the
 *      brief names directly.
 *   2. IT DOES NOT PROMISE AN ALERT THAT CANNOT ARRIVE. `PRIOR_RECOMMENDATION_
 *      NEW_EVIDENCE` is declared and nothing raises it; two more classes can
 *      only be raised by a brief that has already seen one. The producer
 *      coverage is rendered FROM THE PAYLOAD, because it is declared in
 *      `scout-intelligence.ts` beside the calls that raise them — a copy of it
 *      here would be a second answer that drifts.
 *   3. IT DOES NOT LINK TO A VIEW THAT DOES NOT EXIST. Every notification
 *      points at a real durable record by `(pointerKind, pointerRef)`, and the
 *      view that renders that kind is not built yet for ALL of them. So the
 *      pointer is shown in full — it is the thing that survives — and the
 *      destination that will render it is named with its slice, taken from the
 *      same table the navigation is built from. When slice 5 lands, the link
 *      starts working and nothing here has to change.
 *   4. IT DOES NOT FABRICATE A NOTIFICATION. Nothing on this screen creates
 *      one, and the empty state says which producers could have.
 *
 * MARKING READ IS A WRITE AND IS SHOWN AS ONE. The row updates only after the
 * server confirms it, and a failure leaves the row unread with the reason —
 * because a green tick over a write that did not land is the smallest lie this
 * screen could tell.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable, expectsMembers } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { navigate } from '../router';
import { PageHeader, Technical } from './about';
import {
	currentPushSubscription,
	dropPushSubscription,
	endpointFingerprintOf,
	pushCapability,
	subscribeToPush,
} from '../service-worker-client';

interface NotificationRecord {
	notificationId: string;
	targetKey: string | null;
	class: string;
	title: string;
	summary: string;
	pointerKind: string;
	pointerRef: string;
	createdAt: string;
	readAt: string | null;
	archivedAt: string | null;
	archiveReason: string | null;
}

interface InboxPage {
	notifications: NotificationRecord[];
	olderCursor: { createdAt: string; notificationId: string } | null;
	hasOlder: boolean;
	totalCount: number;
	unreadCount: number;
	archivedCount: number;
	unreadOnly: boolean;
	/** Class → who raises it. Carried on the payload, not written here. */
	producers: Record<string, string>;
}

interface MarkedRead {
	marked: boolean;
	notificationId: string;
}

/**
 * WHERE A POINTER'S RECORD IS RENDERED — AND WHERE IT IS NOT YET.
 *
 * THE POINTER KIND IS A CLOSED SET IN PRACTICE, and each kind has exactly one
 * view that will show it. That mapping lives here as a table of DESTINATION IDS
 * rather than of slices, so the slice and the label come from `destinations.ts`
 * — the same table the sidebar is built from. A kind mapped to a destination
 * that is still `pending` renders as the pointer plus an honest explanation;
 * the day that destination is built, the same row becomes a link with no edit
 * to this file.
 */
/**
 * WHERE A POINTER LEADS, IN THE FIVE-AREA ADDRESS SPACE.
 *
 * A notification points at a record, not at a page, so the link names the tab
 * that renders the record and carries the record id when the surface can open
 * it. A kind this build has no view for returns null, and the row says so
 * rather than offering a link that would land nowhere.
 */
interface PointerTarget {
	label: string;
	destination: string;
	section: string | null;
	recordId: string | null;
	href: string;
}

function pointerTarget(kind: string, ref: string): PointerTarget | null {
	switch (kind) {
		case 'self_improvement':
			return {
				label: 'the improvement',
				destination: 'workspace',
				section: 'improvements',
				recordId: ref,
				href: `/workspace/improvements/${encodeURIComponent(ref)}`,
			};
		case 'outcome':
			return {
				label: 'the improvement',
				destination: 'workspace',
				section: 'improvements',
				recordId: ref,
				href: `/workspace/improvements/${encodeURIComponent(ref)}`,
			};
		case 'assessment':
		case 'belief':
			return {
				label: 'Knowledge',
				destination: 'discoveries',
				section: 'knowledge',
				recordId: null,
				href: '/discoveries/knowledge',
			};
		case 'morning_brief':
			return {
				label: 'History & Search',
				destination: 'history',
				section: null,
				recordId: null,
				href: '/history',
			};
		default:
			return null;
	}
}

/** A class name as a reader would say it. */
function humanClass(value: string): string {
	const words = value.toLowerCase().replace(/_/g, ' ');
	return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The page size this view asks for. The server has its own default; naming it
 *  here means the "of N" figure and the page are about the same number. */
const PAGE_SIZE = 25;

/* ------------------------------------------------------------- web push */

/** What `GET /gateway/push` answers. The two key halves and the endpoint are
 *  deliberately NOT part of it — together they are a capability to reach a
 *  device, and a screen has no use for them. Each row carries a short
 *  FINGERPRINT instead, which is enough for the page that owns a subscription
 *  to recognise its own row and nothing more. */
interface PushSubscriptionRow {
	subscriptionId: string;
	label: string | null;
	userAgent: string | null;
	endpointFingerprint: string;
	createdAt: string;
	lastSuccessAt: string | null;
	lastFailureAt: string | null;
	failureCount: number;
	state: 'LIVE' | 'REVOKED' | 'EXPIRED';
}

interface PushDescription {
	wired: boolean;
	publicKey: string | null;
	subscriptions: PushSubscriptionRow[];
	/** Always a sentence: why push is or is not wired, in the terms an operator
	 *  acts on. Rendered verbatim, because the client cannot know. */
	reason: string;
}

function deviceName(row: PushSubscriptionRow): string {
	// THE LABEL THE DEVICE GAVE, OR THE BROWSER'S OWN WORDS, OR NOTHING. What is
	// NEVER shown is the endpoint: it is a capability, and printing it on a
	// screen puts it in a screenshot.
	return row.label ?? row.userAgent ?? 'an unnamed browser';
}

/**
 * TURNING NOTIFICATIONS ON FOR THIS DEVICE — AND SAYING WHAT THAT DOES.
 *
 * THE PERMISSION PROMPT IS RAISED BY A PRESS AND BY NOTHING ELSE. A prompt on
 * page load is a prompt refused on principle, and a browser that has been told
 * "no" cannot be asked again by any page — so the one chance this feature gets
 * must arrive when the Owner has said they want it.
 *
 * THE PANEL IS HONEST IN FOUR STATES, AND THE INTERESTING ONES ARE THE TWO
 * THAT ARE NOT SUCCESS:
 *
 *   not wired        the deployment has no VAPID key. The server's own sentence
 *                    is rendered rather than paraphrased, because the reason is
 *                    a fact about a deployment and this file cannot know it.
 *   permission denied the browser refuses. THE INBOX STILL WORKS — it is a
 *                    durable record read on demand, and it was never dependent
 *                    on push. That sentence is on the screen because a person
 *                    who has just refused a prompt needs to know they have not
 *                    broken anything.
 *   subscribed       the device is listed, with the server's own view of it —
 *                    including a REVOKED or EXPIRED row, which is a record of
 *                    something that happened rather than a row to hide.
 *   subscription lost the browser holds one the server does not. This is a real
 *                    and common state (a revoked row, a cleared profile), and
 *                    the panel offers to register it again rather than
 *                    pretending the device is fine.
 */
function PushPanel() {
	// THE SHAPE IS CHECKED AT THE DOOR, WHICH IS THE RULE THIS WORKSPACE ALREADY
	// HAS AND THIS PANEL ORIGINALLY BROKE. `createLoadable` with an `expects`
	// guard turns a payload this build cannot read into a NAMED FAILURE rendered
	// in the ordinary failure panel — and without the guard the first
	// dereference of `subscriptions` throws inside a Preact render, which does
	// not degrade one panel: it takes the whole workspace down. That is not a
	// theory. It was observed, four times, as four unhandled errors attributed
	// to whichever specimen happened to be running (`resource.ts`'s own header
	// records the same failure from the science views).
	const push = useMemo(
		() =>
			createLoadable<PushDescription>(
				() => readGateway('/api/scout/gateway/push'),
				expectsMembers('wired', 'reason', 'subscriptions'),
			),
		[],
	);
	const [busy, setBusy] = useState(false);
	const [localEndpoint, setLocalEndpoint] = useState<string | null>(null);
	const [outcome, setOutcome] = useState<string | null>(null);
	const [problem, setProblem] = useState<string | null>(null);

	const capability = pushCapability();

	const refresh = useCallback(async () => {
		await push.load();
		const local = await currentPushSubscription().catch(() => null);
		setLocalEndpoint(local?.endpoint ?? null);
	}, [push]);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	async function turnOn(): Promise<void> {
		setBusy(true);
		setProblem(null);
		setOutcome(null);
		try {
			const described = push.state.value.data;
			if (described?.publicKey == null) {
				setProblem('this deployment did not give this page a push key, so nothing could be registered');
				return;
			}
			const result = await subscribeToPush(described.publicKey);
			if (!result.ok) {
				setProblem(result.detail);
				return;
			}
			// THE SERVER IS TOLD AFTER THE BROWSER AGREES, and the order is the
			// point: a row registered for a subscription the browser then
			// refuses to hold would be a device Scout would push to for ever.
			//
			// THE RESPONSE SHAPE IS THE SERVER'S OWN, DECLARED AS IT IS.
			// `refreshed` distinguishes a new row from a device that was already
			// registered and had its keys rewritten — a distinction the server
			// makes, so the client carries it rather than flattening it to a
			// boolean nobody chose.
			const registered = await readGateway<{ refreshed: boolean; subscription: { subscriptionId: string } }>(
				'/api/scout/gateway/push/subscriptions',
				{
					method: 'POST',
					body: {
						endpoint: result.subscription.endpoint,
						keys: result.subscription.keys,
						label: typeof navigator === 'undefined' ? null : navigator.userAgent.slice(0, 120),
					},
				},
			);
			if (!registered.ok) {
				// THE BROWSER'S SUBSCRIPTION IS DROPPED AGAIN, because the server
				// would not hold it and leaving it would mean the device believes
				// it is subscribed when Scout has no record of it.
				await dropPushSubscription().catch(() => false);
				setProblem(
					`the browser subscribed, and Scout refused to record it: ${registered.error.name} — ${registered.error.message}`,
				);
				return;
			}
			setOutcome('This device is registered. Scout will send it a generic notice when a notification is recorded.');
			await refresh();
		} finally {
			setBusy(false);
		}
	}

	async function turnOff(subscriptionId: string): Promise<void> {
		setBusy(true);
		setProblem(null);
		setOutcome(null);
		try {
			// THE SERVER IS TOLD FIRST, so there is no window in which the
			// browser has stopped and Scout is still sending.
			//
			// `revoked: false` IS NOT AN ERROR — it is how the caller learns the
			// row was already revoked and nothing changed. The server's own
			// `reason` says which, and it is rendered rather than replaced by a
			// sentence this file made up.
			const revoked = await readGateway<{ subscriptionId: string; revoked: boolean; reason: string }>(
				`/api/scout/gateway/push/subscriptions/${encodeURIComponent(subscriptionId)}/revoke`,
				{ method: 'POST', body: {} },
			);
			if (!revoked.ok) {
				setProblem(`Scout did not record the revocation: ${revoked.error.name} — ${revoked.error.message}`);
				return;
			}
			// THE BROWSER'S HALF IS DROPPED EITHER WAY. A row that was already
			// revoked still leaves this device holding a subscription the server
			// will never deliver to, and leaving it would make the next press of
			// "notify this device" take a path nobody intended.
			const dropped = await dropPushSubscription().catch(() => false);
			setOutcome(
				`${revoked.data.reason} ${
					dropped
						? 'This browser has also dropped its subscription.'
						: 'The browser did not confirm it dropped its subscription — the endpoint is revoked on Scout’s side either way, so nothing more will be sent.'
				}`,
			);
			await refresh();
		} finally {
			setBusy(false);
		}
	}

	const state = push.state.value;
	const described = state.data;
	// THE MEMBER CHECK PROVES THE KEY IS PRESENT; IT DOES NOT PROVE IT IS AN
	// ARRAY. `expectsMembers` is a door against a payload from a different
	// revision, not a schema, and the two lines below are what make the render
	// TOTAL rather than merely usually-correct.
	const subscriptions = Array.isArray(described?.subscriptions) ? described.subscriptions : [];
	// THE DEVICE'S OWN SUBSCRIPTION, MATCHED TO THE SERVER'S ROW BY FINGERPRINT
	// — which is the only identifier the two sides can share. The endpoint
	// itself is never in the payload (it is a capability to reach a device), so
	// the browser can only recognise its own row by computing the same short
	// digest and comparing. `endpointFingerprintOf` is the client half of that
	// derivation and `scripts/scout-web-*` pins the two against each other.
	const mine =
		localEndpoint === null
			? undefined
			: subscriptions.find(
					(row) => row.state === 'LIVE' && row.endpointFingerprint === endpointFingerprintOf(localEndpoint),
				);

	return (
		<section class="panel" aria-labelledby="push-heading">
			<h2 id="push-heading">Notifications on this device</h2>

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Asking Scout whether it can send a push…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the push configuration" onRetry={() => void refresh()} />
			)}

			{described !== null && (
				<>
					<p class="push-state">
						{described.wired ? (
							<span class="tag tag-live">wired</span>
						) : (
							<span class="tag tag-warn">not wired</span>
						)}{' '}
						{/* THE SERVER'S OWN SENTENCE, VERBATIM. It knows which VAPID
						    variable is missing and this file does not. */}
						<span class="muted small">{described.reason}</span>
					</p>

					{capability.kind === 'unsupported' && (
						<p class="push-detail">
							<strong>This browser cannot receive a push.</strong> {capability.detail}. The inbox below
							is unaffected: it is a durable record Scout keeps, read whenever you open it.
						</p>
					)}

					{capability.kind === 'permission' && capability.permission === 'denied' && (
						<p class="push-detail">
							<strong>This browser has been told to refuse notifications for Scout.</strong> That
							setting belongs to the browser, not to this workspace, and a page cannot ask again —
							it has to be changed in the browser’s own site settings. Nothing else is affected:
							the inbox below is complete without push.
						</p>
					)}

					{described.wired && capability.kind !== 'unsupported' && capability.permission !== 'denied' && (
						<p>
							{mine === undefined ? (
								<button
									type="button"
									class="button"
									disabled={busy}
									onClick={() => void turnOn()}
								>
									{busy ? 'Registering…' : 'Notify this device'}
								</button>
							) : (
								<button
									type="button"
									class="button ghost"
									disabled={busy}
									onClick={() => void turnOff(mine.subscriptionId)}
								>
									{busy ? 'Stopping…' : 'Stop notifying this device'}
								</button>
							)}
						</p>
					)}

					{outcome !== null && (
						<p class="push-detail" role="status">
							{outcome}
						</p>
					)}
					{problem !== null && (
						<p class="push-detail" role="alert">
							<strong>Not done.</strong> {problem}
						</p>
					)}

					{/* THE SERVER'S VIEW OF EVERY DEVICE, INCLUDING THE ONES THAT
					    STOPPED. A REVOKED row is a record of something that
					    happened, and hiding it would make "why did this phone stop
					    buzzing" unanswerable. */}
					{subscriptions.length > 0 && (
						<>
							<h3 class="subhead">Devices Scout has a record for</h3>
							<ul class="device-list">
								{subscriptions.map((row) => (
									<li key={row.subscriptionId}>
										<strong>{deviceName(row)}</strong>
										<span class={`tag${row.state === 'LIVE' ? ' tag-live' : ' tag-warn'}`}>
											{row.state}
										</span>
										<span class="muted">
											registered <time dateTime={row.createdAt}>{row.createdAt}</time>
											{row.lastSuccessAt !== null && (
												<>
													{' '}
													· last delivered{' '}
													<time dateTime={row.lastSuccessAt}>{row.lastSuccessAt}</time>
												</>
											)}
											{row.failureCount > 0 && ` · ${row.failureCount} failed attempt(s)`}
										</span>
									</li>
								))}
							</ul>
						</>
					)}

					<p class="push-detail">
						<strong>What a push carries.</strong> A generic sentence and a link to this screen. It never
						carries a finding, a conversation, a source, a model name or an identifier — a lock screen
						is not authenticated, so nothing that needs a session may appear on one. What it says is
						that there is something here, and reading it means opening Scout.
					</p>
				</>
			)}
		</section>
	);
}

export function NotificationsView() {
	const [unreadOnly, setUnreadOnly] = useState(false);
	/** Pages already read, oldest-last. Held as state rather than in the
	 *  loadable because appending is not what a loadable is for — a reload
	 *  replaces, and paging adds. */
	const [older, setOlder] = useState<NotificationRecord[]>([]);
	const [olderError, setOlderError] = useState<GatewayError | null>(null);
	const [paging, setPaging] = useState(false);
	/** Rows the server has confirmed read THIS session. The list is a read of
	 *  one moment, so without this a marked row would keep saying "unread"
	 *  until a reload — and the Owner would mark it again. */
	const [readHere, setReadHere] = useState<Record<string, true>>({});
	const [markError, setMarkError] = useState<{ id: string; error: GatewayError } | null>(null);

	const feed = useMemo(
		() =>
			createLoadable<InboxPage>(() =>
				readGateway<InboxPage>(
					`/api/scout/gateway/notifications?unreadOnly=${unreadOnly ? 'true' : 'false'}&limit=${PAGE_SIZE}`,
				),
			),
		// THE FILTER IS PART OF THE LOADABLE, so switching it starts a fresh
		// first page rather than appending a second filter's rows to the first's.
		[unreadOnly],
	);

	useEffect(() => {
		setOlder([]);
		setOlderError(null);
		void feed.load();
	}, [feed]);

	const state = feed.state.value;
	const data = state.data;

	const loadOlder = useCallback(async () => {
		if (state.status !== 'ready' || data === null) return;
		// THE OLDEST ROW ALREADY SHOWN plus everything appended after it. The
		// cursor is the pair the server orders on, so a page boundary cannot
		// repeat or skip a row.
		const last = older.length > 0 ? older[older.length - 1] : data.notifications[data.notifications.length - 1];
		if (last === undefined) return;
		setPaging(true);
		setOlderError(null);
		const cursor = `${encodeURIComponent(last.createdAt)}|${encodeURIComponent(last.notificationId)}`;
		const result = await readGateway<InboxPage>(
			`/api/scout/gateway/notifications?unreadOnly=${unreadOnly ? 'true' : 'false'}&limit=${PAGE_SIZE}&before=${cursor}`,
		);
		setPaging(false);
		if (!result.ok) {
			setOlderError(result.error);
			return;
		}
		setOlder((previous) => [...previous, ...result.data.notifications]);
	}, [state.status, data, older, unreadOnly]);

	const markRead = useCallback(async (notificationId: string) => {
		setMarkError(null);
		const result = await readGateway<MarkedRead>(
			`/api/scout/gateway/notifications/${encodeURIComponent(notificationId)}/read`,
			{ method: 'POST', body: {} },
		);
		if (!result.ok) {
			// THE ROW STAYS UNREAD AND SAYS WHY. Updating it anyway would show a
			// state the server does not hold, and the Owner would find it unread
			// again on the next read with nothing to explain the difference.
			setMarkError({ id: notificationId, error: result.error });
			return;
		}
		setReadHere((previous) => ({ ...previous, [notificationId]: true }));
	}, []);

	const all = [...(data?.notifications ?? []), ...older];
	const shown = unreadOnly
		? all.filter((item) => item.readAt === null && readHere[item.notificationId] !== true)
		: all;
	// THE UNREAD COUNT MOVES WITH THE MARKS MADE ON THIS SCREEN, AND ONLY WITH
	// THOSE. The payload's `unreadCount` is a read of one moment; without this
	// adjustment, marking a row read while filtered to "unread only" would remove
	// the row and leave the count saying it was still there — a small, checkable
	// falsehood, and exactly the kind this product does not print. Only rows that
	// ARRIVED unread are subtracted, so a row already read on the server cannot
	// be double-counted, and the figure can never go below zero.
	const markedReadHere = all.filter(
		(item) => readHere[item.notificationId] === true && item.readAt === null,
	).length;
	const liveUnread = Math.max(0, (data?.unreadCount ?? 0) - markedReadHere);

	return (
		<section class="view" aria-labelledby="notifications-heading">
			<PageHeader
				id="notifications-heading"
				title="Notifications"
				about={{
					purpose:
						'Every notification Scout has recorded for you, newest first, with the durable record each one points at.',
					how: (
						<p>
							Use “Unread only” to see what still needs you. Marking a notification read is a write;
							it says you saw the inbox entry, and it never changes the lifecycle of the record the
							entry points at.
						</p>
					),
					limits: (
						<>
							<p>
								<strong>Read state is the only state this inbox carries.</strong> There is no
								archive, no snooze, no priority and no grouping.
							</p>
							<p>
								<strong>A push is not the notification.</strong> The inbox above is complete whether
								or not a device was ever registered, and a push carries one generic sentence and a
								link back here — never a finding, a source or an identifier.
							</p>
							<p>
								<strong>A notification is a pointer, never the evidence.</strong> The record behind
								the pointer is what is authoritative.
							</p>
						</>
					),
				}}
			/>

			{/* THE FILTER IS TWO REAL CHOICES, NOT A DECORATION. "Everything" is
			    what the inbox holds; "unread" is what still needs the Owner. */}
			<div class="filterbar" role="group" aria-label="Which notifications to show">
				<button
					type="button"
					class={`button${unreadOnly ? '' : ' is-active'}`}
					aria-pressed={!unreadOnly}
					onClick={() => setUnreadOnly(false)}
				>
					Everything
				</button>
				<button
					type="button"
					class={`button${unreadOnly ? ' is-active' : ''}`}
					aria-pressed={unreadOnly}
					onClick={() => setUnreadOnly(true)}
				>
					Unread only
				</button>
			</div>

			{state.status === 'loading' && (
				<p class="notice" role="status">
					Reading Scout’s inbox…
				</p>
			)}
			{state.status === 'failed' && state.error !== null && (
				<Failure error={state.error} what="the inbox" onRetry={() => void feed.load()} />
			)}

			{state.status === 'ready' && data !== null && (
				<>
					{/* THE DEVICE PANEL SITS ABOVE THE RECORD, because it is a SETTING
					    rather than a notification — and because a person who has just
					    been refused a permission prompt should meet the sentence
					    explaining that before they scroll past it. */}
					<PushPanel />

					<p class="countline">
						Showing <span class="count">{shown.length}</span> of{' '}
						<span class="count">{unreadOnly ? liveUnread : data.totalCount}</span>{' '}
						{unreadOnly ? 'unread notification(s)' : 'notification(s) recorded'}
						{!unreadOnly && liveUnread > 0 && (
							<>
								{' '}
								— <span class="count">{liveUnread}</span> of them unread
							</>
						)}
						{data.hasOlder || older.length > 0 ? (
							<span class="muted"> · older ones are below</span>
						) : (
							<span class="muted"> · this is all of them</span>
						)}
					</p>

					{shown.length === 0 ? (
						<p>
							<strong>
								{unreadOnly
									? 'Nothing is unread.'
									: 'Scout has recorded no notifications for you.'}
							</strong>{' '}
							<span class="muted">
								The table below says which classes could have produced one and which nothing
								produces — an empty inbox is a fact about the producers, not evidence that
								nothing happened.
							</span>
						</p>
					) : (
						<ul class="records">
							{shown.map((item) => (
								<NotificationRow
									key={item.notificationId}
									item={item}
									read={item.readAt !== null || readHere[item.notificationId] === true}
									markError={markError?.id === item.notificationId ? markError.error : null}
									onMarkRead={() => void markRead(item.notificationId)}
								/>
							))}
						</ul>
					)}

					{olderError !== null && (
						<Failure error={olderError} what="the older notifications" onRetry={() => void loadOlder()} />
					)}
					{/* THE BUTTON APPEARS ONLY WHEN THERE IS MORE, and says how many
					    are left rather than just "more" — a control whose size the
					    reader cannot judge is one they press to find out. */}
					{(data.hasOlder || (older.length > 0 && older.length < data.totalCount)) && (
						<p>
							<button type="button" class="button" disabled={paging} onClick={() => void loadOlder()}>
								{paging ? 'Reading…' : 'Read older notifications'}
							</button>
						</p>
					)}

					<section class="panel" aria-labelledby="producers-heading">
						<h2 id="producers-heading">What can raise a notification here</h2>
						<p class="muted small">
							Scout declares eight notification classes. Declaring one is not the same as producing
							it, and an inbox that has never shown a class looks exactly like one whose situation
							has never arisen. This is what the source says each one is raised by.
						</p>
						<ul class="records">
							{Object.entries(data.producers).map(([className, producer]) => (
								<li key={className} class="record">
									<div class="record-head">
										<code>{className}</code>
										{/* THE ONES NOTHING RAISES ARE MARKED, not buried in
										    prose. A reader scanning this list has to be able to
										    see which classes cannot arrive. */}
										{producer.startsWith('NOTHING') && <span class="tag tag-warn">no producer</span>}
									</div>
									<p class="muted small">{producer}</p>
								</li>
							))}
						</ul>
					</section>

				</>
			)}
		</section>
	);
}

function NotificationRow({
	item,
	read,
	markError,
	onMarkRead,
}: {
	item: NotificationRecord;
	read: boolean;
	markError: GatewayError | null;
	onMarkRead: () => void;
}) {
	const target = pointerTarget(item.pointerKind, item.pointerRef);
	return (
		<li class={`record${read ? ' is-read' : ''}`}>
			<div class="record-head">
				<strong>{item.title}</strong>
				<span class="tag">{humanClass(item.class)}</span>
				{item.archivedAt !== null && (
					<span class="tag" title="This issue was verified resolved; its read state was not changed.">
						automatically resolved
					</span>
				)}
				{!read && item.archivedAt === null && <span class="tag tag-live">unread</span>}
			</div>
			{/* WHY, NOT JUST WHAT. Scout's own requirement: a notification has to
			    carry the reason it exists, and this is the field the producers
			    write it into. It is rendered verbatim. */}
			<p class="record-summary">{item.summary}</p>
			<p class="muted small">
				<time dateTime={item.createdAt}>{item.createdAt}</time>
				{item.targetKey !== null && (
					<>
						{' '}
						· target <code>{item.targetKey}</code>
					</>
				)}
			</p>
			{/* THE POINTER IS SHOWN WHATEVER ELSE IS TRUE, because it is the part
			    that survives: it names the record, it is what a later surface
			    resolves, and it is checkable by hand against the API. */}
			<p class="pointer small">
				{target === null ? (
					<span class="muted">This build names no view for that kind of record.</span>
				) : (
					<>
						<a
							href={target.href}
							onClick={(event: MouseEvent) => {
								if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
								event.preventDefault();
								navigate({
									destination: target.destination,
									section: target.section,
									recordId: target.recordId,
								});
							}}
						>
							Open {target.label}
						</a>
					</>
				)}
			</p>
			<Technical summary="Technical pointer">
				<p class="small">
					points at <code>{item.pointerKind}</code> <code>{item.pointerRef}</code>
				</p>
			</Technical>
			{!read && item.archivedAt === null && (
				<p>
					<button type="button" class="button ghost" onClick={onMarkRead}>
						Mark as read
					</button>
				</p>
			)}
			{item.archivedAt !== null && (
				<p class="small muted">
					{item.archiveReason ?? 'The issue this pointed at was verified resolved.'} The record is
					kept; its read state was not changed.
				</p>
			)}
			{markError !== null && (
				<p class="small" role="alert">
					<strong>Not marked read.</strong> Scout did not accept it: <code>{markError.name}</code> —{' '}
					{markError.message}
				</p>
			)}
		</li>
	);
}
